// © 2026 AttainIP Engineering
/**
 * Spinner — app-wide loading indicator. Uses the Halmos animation
 * desaturated to grey, so every "loading" moment in the app reads as
 * the same visual primitive.
 */
import { HalmosAnimation } from './HalmosAnimation';

interface SpinnerProps {
  /** Width/height in pixels (default: 16) */
  size?: number;
  className?: string;
}

export function Spinner({ size = 16, className }: SpinnerProps) {
  return <HalmosAnimation variant="grey" size={size} className={className} />;
}

interface LoadingPlaceholderProps {
  /** Message displayed next to the spinner */
  message?: string;
  /** Size of the spinner in pixels (default: 20) */
  size?: number;
  /** Stack the Halmos above the text (centred) instead of side-by-side. */
  vertical?: boolean;
}

/**
 * Full loading placeholder: spinner + message. Drop-in replacement for the
 * ss-lib LoadingPlaceholder, but uses the Halmos spinner for app-wide
 * consistency. `vertical` opts a single call-site into a stacked, centred
 * layout (Halmos above, text below) without affecting the default horizontal
 * usage elsewhere.
 */
export function LoadingPlaceholder({ message = 'Loading...', size = 20, vertical = false }: LoadingPlaceholderProps) {
  return (
    <div
      className={`flex ${vertical ? 'flex-col text-center' : ''} items-center justify-center gap-ds-2 py-ds-6 text-ds-text-secondary text-[13px] h-full min-h-50`}
    >
      <Spinner size={size} />
      <span>{message}</span>
    </div>
  );
}
